import { useCallback, useMemo, useRef, useState } from 'react'
import { interviewService, ApiError } from '@/services'
import type {
  ContextGap,
  GapStatus,
  Message,
  InterviewResponse,
} from '@/types/api'
import { useGapSessions } from './useGapSessions'
import {
  adjustCount,
  appendMessage,
  effectiveStatuses,
  firstResolvedGapId,
  makeOptimisticMessage,
  prependMessage,
  removeMessage,
  withoutKey,
  type MessagesByGap,
} from './fillGapsSessionHelpers'

interface UseFillGapsSessionOptions {
  startupId: string | null
  /** Every gap on the page. Used for status checks + resolved-toast titles. */
  gaps: ContextGap[]
  /** Rows currently expanded. A row holds a session only while open. */
  expandedGapIds: string[]
}

export interface UseFillGapsSessionResult {
  /** Per-gap threads. A row's thread resets when the row collapses. */
  messagesByGap: Record<string, Message[]>
  /** Local status overrides that win over the fetched list until the
   * next refetch — drives the row dropping out of the active list. */
  statusOverrides: Record<string, GapStatus>
  /** True for a gap while one of its sends is in flight. */
  busyByGap: Record<string, boolean>
  /** Last gap that resolved — fed to the toast. Cleared on dismiss. */
  recentlyResolved: { id: string; title: string } | null
  clearRecentlyResolved: () => void
  /** Last session-start failure — null when every open row is healthy. */
  sessionError: string | null
  /** Retry the failed session starts. */
  retrySession: () => void
  /** Send a message on the gap's own session (starting it if needed). */
  sendMessage: (gapId: string, content: string) => Promise<void>
  /** Apply a gap-status delta from outside (e.g. per-gap upload). */
  applyStatusUpdates: (updates: Record<string, GapStatus>) => void
}

/**
 * Owns Fill Gaps conversations. Sessions are per gap and lock-on-expand
 * (see `useGapSessions`), so opening the page holds nothing. `gapUpdates`
 * from each send feed local status overrides so resolved rows collapse
 * without waiting for the next gap-analysis refetch.
 */
export function useFillGapsSession({
  startupId,
  gaps,
  expandedGapIds,
}: UseFillGapsSessionOptions): UseFillGapsSessionResult {
  const [messagesByGap, setMessagesByGap] = useState<MessagesByGap>({})
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, GapStatus>
  >({})
  const [sendingCounts, setSendingCounts] = useState<Record<string, number>>({})
  const [recentlyResolved, setRecentlyResolved] = useState<
    { id: string; title: string } | null
  >(null)

  // Latest values in refs so sendMessage stays stable across gap refetches.
  const gapsRef = useRef(gaps)
  gapsRef.current = gaps
  const statusById = useMemo(
    () => effectiveStatuses(gaps, statusOverrides),
    [gaps, statusOverrides]
  )
  const statusByIdRef = useRef(statusById)
  statusByIdRef.current = statusById

  // Per-gap thread generation, bumped when a row collapses. A send only
  // writes to the thread it started in, so a reply that lands after the
  // collapse can't leak into the next session's thread.
  const threadGenRef = useRef(new Map<string, number>())

  const onInitialMessage = useCallback((gapId: string, message: Message) => {
    setMessagesByGap((prev) => prependMessage(prev, gapId, message))
  }, [])
  // A collapsed row's session is gone, so its thread would no longer match
  // what the assistant remembers. Start clean on the next expand.
  const onRelease = useCallback((gapId: string) => {
    const gens = threadGenRef.current
    gens.set(gapId, (gens.get(gapId) ?? 0) + 1)
    setMessagesByGap((prev) => withoutKey(prev, gapId))
  }, [])

  /** Thread updater bound to the gap's current thread; no-op once released. */
  const threadWriter = useCallback((gapId: string) => {
    const gen = threadGenRef.current.get(gapId) ?? 0
    return (update: (prev: MessagesByGap) => MessagesByGap) => {
      if ((threadGenRef.current.get(gapId) ?? 0) === gen) setMessagesByGap(update)
    }
  }, [])

  const { sessionError, retrySession, ensureSession } = useGapSessions({
    startupId,
    expandedGapIds,
    statusById,
    onInitialMessage,
    onRelease,
  })

  const applyResponseUpdates = useCallback((response: InterviewResponse) => {
    const updates = response.gapUpdates ?? {}
    if (Object.keys(updates).length === 0) return
    setStatusOverrides((prev) => ({ ...prev, ...updates }))
    const resolvedId = firstResolvedGapId(updates)
    if (!resolvedId) return
    const gap = gapsRef.current.find((g) => g.id === resolvedId)
    setRecentlyResolved({
      id: resolvedId,
      title: gap?.title?.trim() || gap?.description || 'Gap resolved',
    })
  }, [])

  const sendMessage = useCallback(
    async (gapId: string, content: string) => {
      const trimmed = content.trim()
      if (!startupId || !trimmed) return
      if (statusByIdRef.current[gapId] === 'resolved') return

      const writeThread = threadWriter(gapId)
      // Kick off (or join) the session start before the optimistic bubble
      // so a still-starting session doesn't make the user wait to see it.
      const sessionPromise = ensureSession(gapId)
      const optimistic = makeOptimisticMessage(trimmed)
      const dropOptimistic = () =>
        writeThread((prev) => removeMessage(prev, gapId, optimistic.id))
      writeThread((prev) => appendMessage(prev, gapId, optimistic))
      setSendingCounts((prev) => adjustCount(prev, gapId, 1))
      try {
        const sessionId = await sessionPromise
        // No session: the start failed and the page banner offers Retry.
        if (!sessionId) {
          dropOptimistic()
          return
        }
        const response = await interviewService.sendMessage(startupId, sessionId, {
          content: trimmed,
          activeGapId: gapId,
          clientTimestamp: optimistic.createdAt,
        })
        const reply = response.message
        if (reply) writeThread((prev) => appendMessage(prev, gapId, reply))
        // Status changes describe the server, so they apply even when the
        // row collapsed mid-send and the reply itself was dropped.
        applyResponseUpdates(response)
      } catch (err) {
        // Logging happens at the apiClient layer.
        dropOptimistic()
        if (!(err instanceof ApiError && err.status === 401)) throw err
      } finally {
        setSendingCounts((prev) => adjustCount(prev, gapId, -1))
      }
    },
    [startupId, threadWriter, ensureSession, applyResponseUpdates]
  )

  const applyStatusUpdates = useCallback(
    (updates: Record<string, GapStatus>) => {
      if (Object.keys(updates).length === 0) return
      setStatusOverrides((prev) => ({ ...prev, ...updates }))
    },
    []
  )

  const clearRecentlyResolved = useCallback(() => setRecentlyResolved(null), [])
  const busyByGap = useMemo(
    () =>
      Object.fromEntries(Object.keys(sendingCounts).map((id) => [id, true])),
    [sendingCounts]
  )

  return {
    messagesByGap,
    statusOverrides,
    busyByGap,
    recentlyResolved,
    clearRecentlyResolved,
    sessionError,
    retrySession,
    sendMessage,
    applyStatusUpdates,
  }
}
